import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ArtikelZulage, ArtikelZulageFilter } from '../shared/types/artikelZulage';
import type { RootState } from '../configureStore';

export const GET_ARTIKEL_ZULAGEN = 'GET_ARTIKEL_ZULAGEN';
export const GET_ARTIKEL_ZULAGEN_SUCCEEDED = 'GET_ARTIKEL_ZULAGEN_SUCCEEDED';
export const GET_ARTIKEL_ZULAGEN_FAILED = 'GET_ARTIKEL_ZULAGEN_FAILED';

export interface GetArtikelZulagenAction {
    type: typeof GET_ARTIKEL_ZULAGEN;
    filter: ArtikelZulageFilter;
}

export interface GetArtikelZulagenSucceededAction {
    type: typeof GET_ARTIKEL_ZULAGEN_SUCCEEDED;
    zulagen: ArtikelZulage[];
    filter: ArtikelZulageFilter;
}

export interface GetArtikelZulagenFailedAction {
    type: typeof GET_ARTIKEL_ZULAGEN_FAILED;
}

export const getArtikelZulagen = (filter: ArtikelZulageFilter): GetArtikelZulagenAction => ({
    type: GET_ARTIKEL_ZULAGEN,
    filter,
});

export const getArtikelZulagenSucceeded = (payload: {
    zulagen: ArtikelZulage[];
    filter: ArtikelZulageFilter;
}): GetArtikelZulagenSucceededAction => ({
    type: GET_ARTIKEL_ZULAGEN_SUCCEEDED,
    zulagen: payload.zulagen,
    filter: payload.filter,
});

export const getArtikelZulagenFailed = (): GetArtikelZulagenFailedAction => ({
    type: GET_ARTIKEL_ZULAGEN_FAILED,
});

export interface ArtikelZulageState {
    zulagenByFilter: Record<string, ArtikelZulage[]>;
    isLoading: boolean;
    loadFailed: boolean;
    loadFinished: boolean;
    currentFilter?: ArtikelZulageFilter;
}

const initialState: ArtikelZulageState = {
    zulagenByFilter: {},
    isLoading: false,
    loadFailed: false,
    loadFinished: false,
    currentFilter: undefined,
};

const artikelZulageSlice = createSlice({
    name: 'artikelZulage',
    initialState,
    reducers: {
        getArtikelZulagen: (state, action: PayloadAction<ArtikelZulageFilter>) => {
            state.isLoading = true;
            state.loadFailed = false;
            state.loadFinished = false;
            state.currentFilter = action.payload;
        },
        getArtikelZulagenSucceeded: (state, action: PayloadAction<{ zulagen: ArtikelZulage[]; filter: ArtikelZulageFilter }>) => {
            const filterKey = `${action.payload.filter.artikelgruppeId}-${action.payload.filter.werkId || 'no-werk'}-${
                action.payload.filter.partnerNummer
            }`;
            state.zulagenByFilter[filterKey] = action.payload.zulagen;
            state.currentFilter = action.payload.filter; // Wichtig: currentFilter setzen!
            state.isLoading = false;
            state.loadFailed = false;
            state.loadFinished = true;

            console.log('🔧 Store Update:');
            console.log('  🔑 filterKey:', filterKey);
            console.log('  📦 zulagen:', action.payload.zulagen);
            console.log('  📊 zulagenByFilter:', state.zulagenByFilter);
            console.log('  🎯 currentFilter:', state.currentFilter);
        },
        getArtikelZulagenFailed: (state) => {
            state.isLoading = false;
            state.loadFailed = true;
            state.loadFinished = true;
        },
        clearArtikelZulagen: (state) => {
            state.zulagenByFilter = {};
            state.currentFilter = undefined;
        },
    },
});

export const selectArtikelZulagen = (state: RootState): ArtikelZulage[] => {
    const currentFilter = state.artikelZulage.currentFilter;
    if (!currentFilter) {
        console.log('🔍 Selector: Kein currentFilter');
        console.log('  📊 zulagenByFilter:', state.artikelZulage.zulagenByFilter);
        return [];
    }

    const filterKey = `${currentFilter.artikelgruppeId}-${currentFilter.werkId || 'no-werk'}-${currentFilter.partnerNummer}`;
    const zulagen = state.artikelZulage.zulagenByFilter[filterKey] || [];

    console.log('🔍 Selector:');
    console.log('  🎯 currentFilter:', currentFilter);
    console.log('  🔑 filterKey:', filterKey);
    console.log('  📦 zulagen:', zulagen);
    console.log('  📊 zulagenByFilter:', state.artikelZulage.zulagenByFilter);

    return zulagen;
};

// Neuer Selector, der einen spezifischen Filter verwendet
export const selectArtikelZulagenByFilter = (state: RootState, filter: ArtikelZulageFilter): ArtikelZulage[] => {
    const filterKey = `${filter.artikelgruppeId}-${filter.werkId || 'no-werk'}-${filter.partnerNummer}`;
    const zulagen = state.artikelZulage.zulagenByFilter[filterKey] || [];

    console.log('🔍 SelectorByFilter:');
    console.log('  🎯 filter:', filter);
    console.log('  🔑 filterKey:', filterKey);
    console.log('  📦 zulagen:', zulagen);

    return zulagen;
};

export const selectArtikelZulagenLoading = (state: RootState): boolean => state.artikelZulage.isLoading;
export const selectArtikelZulagenLoadFailed = (state: RootState): boolean => state.artikelZulage.loadFailed;
export const selectArtikelZulagenLoadFinished = (state: RootState): boolean => state.artikelZulage.loadFinished;
export const selectArtikelZulagenCurrentFilter = (state: RootState): ArtikelZulageFilter | undefined => state.artikelZulage.currentFilter;

export const selectAktiveArtikelZulagen = (state: RootState): ArtikelZulage[] => {
    const zulagen = selectArtikelZulagen(state);
    return zulagen.filter((zulage: ArtikelZulage) => zulage.aktiv);
};

// Neuer Selector für aktive Zulagen mit spezifischem Filter
export const selectAktiveArtikelZulagenByFilter = (state: RootState, filter: ArtikelZulageFilter): ArtikelZulage[] => {
    const zulagen = selectArtikelZulagenByFilter(state, filter);
    return zulagen.filter((zulage: ArtikelZulage) => zulage.aktiv);
};

export const ArtikelZulageSelectors = {
    zulagen: selectArtikelZulagen,
    loading: selectArtikelZulagenLoading,
    loadFailed: selectArtikelZulagenLoadFailed,
    loadFinished: selectArtikelZulagenLoadFinished,
    currentFilter: selectArtikelZulagenCurrentFilter,
    aktiveZulagen: selectAktiveArtikelZulagen,
    zulagenByFilter: selectArtikelZulagenByFilter,
    aktiveZulagenByFilter: selectAktiveArtikelZulagenByFilter,
};

export const {
    getArtikelZulagen: getArtikelZulagenAction,
    getArtikelZulagenSucceeded: getArtikelZulagenSucceededAction,
    getArtikelZulagenFailed: getArtikelZulagenFailedAction,
    clearArtikelZulagen,
} = artikelZulageSlice.actions;

export const artikelZulageReducer = artikelZulageSlice.reducer;
